import { useRef, useState, useEffect } from "react";
import { promoterApi, getAccessToken } from "../../api/client";
import { Eye, EyeOff } from "lucide-react";

const CreatePromoter = () => {
  const form = useRef();
  const submissionDataRef = useRef(null); // store prepared FormData until confirmation
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // modal / verification states
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [utrId, setUtrId] = useState("");
  const [qrData, setQrData] = useState("");

  useEffect(() => {
    // close & cancel creation on Escape
    const onKey = (e) => {
      if (showVerifyModal && e.key === "Escape") {
        cancelVerification();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showVerifyModal]);

  const validatePAN = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // if modal already open, do not proceed
    if (showVerifyModal) return;

    setError("");
    setMessage("");

    const formData = new FormData(form.current);

    // Validation
    const name = formData.get("name")?.trim();
    const fatherName = formData.get("father_name")?.trim();
    const email = formData.get("email")?.trim();
    let phoneNumber = formData.get("phone_number")?.trim();
    const bloodGroup = formData.get("blood_group");
    const qualification = formData.get("qualification")?.trim();
    const street = formData.get("street")?.trim();
    const city = formData.get("city")?.trim();
    const state = formData.get("state")?.trim();
    const pincode = formData.get("pincode")?.trim();
    const country = formData.get("country")?.trim();
    const panNumber = formData.get("pancard_number")?.toUpperCase().trim();
    const passwordValue = password.trim();
    const confirmPasswordValue = confirmPassword.trim();

    // Format phone number - if it's 10 digits, prepend +91
    if (
      phoneNumber &&
      phoneNumber.length === 10 &&
      !phoneNumber.startsWith("+")
    ) {
      phoneNumber = `+91${phoneNumber}`;
    }

    if (!name) return setError("Full name is required");
    if (!fatherName) return setError("Father's name is required");
    if (!isValidEmail(email)) return setError("Valid email is required");
    if (!phoneNumber || phoneNumber.length < 10)
      return setError("Valid 10-digit phone number is required");
    if (!bloodGroup) return setError("Blood group is required");
    if (!qualification) return setError("Qualification is required");
    if (!street) return setError("Street address is required");
    if (!city) return setError("City is required");
    if (!state) return setError("State is required");
    if (!pincode) return setError("Pincode is required");
    if (!validatePAN(panNumber))
      return setError("Invalid PAN number! Format: ABCDE1234F");
    if (!passwordValue || passwordValue.length < 6)
      return setError("Password must be at least 6 characters long");
    if (passwordValue !== confirmPasswordValue)
      return setError("Passwords do not match");

    // Check required files
    const requiredFiles = [
      "qualification_doc",
      "pancard_file",
      "id_upload",
      "cv",
      "photo_upload",
    ];
    for (const fileField of requiredFiles) {
      if (!formData.get(fileField) || formData.get(fileField).size === 0) {
        return setError(`${fileField.replace(/_/g, " ")} is required`);
      }
    }

    // Prepare submission data but do NOT send yet
    const submissionData = new FormData();
    submissionData.append("name", name);
    submissionData.append("fatherName", fatherName);
    submissionData.append("email", email);
    submissionData.append("phoneNumber", phoneNumber);
    submissionData.append("password", passwordValue);
    submissionData.append("bloodGroup", bloodGroup);
    submissionData.append("qualification", qualification);
    submissionData.append("panNumber", panNumber);
    submissionData.append("street", street);
    submissionData.append("city", city);
    submissionData.append("state", state);
    submissionData.append("pincode", pincode);
    submissionData.append("country", country);
    submissionData.append(
      "experienceDocuments",
      formData.get("qualification_doc")
    );
    submissionData.append("panCardImage", formData.get("pancard_file"));
    submissionData.append("kycDocument", formData.get("id_upload"));
    submissionData.append("cvUpload", formData.get("cv"));
    submissionData.append("photoUpload", formData.get("photo_upload"));

    // store for later submission
    submissionDataRef.current = submissionData;

    // generate PAYMENT QR payload for ₹299 and open modal
    const upiId = "9818877199-9@ybl";
    const upiName = "Online Indian Market";
    const amount = "299.00";
    const txnNote = "Promoter Creation";
    const upiLink = `upi://pay?pa=${encodeURIComponent(
      upiId
    )}&pn=${encodeURIComponent(upiName)}&am=${encodeURIComponent(
      amount
    )}&cu=INR&tn=${encodeURIComponent(txnNote)}`;

    setQrData(upiLink);
    setShowVerifyModal(true);
  };

  const cancelVerification = () => {
    setShowVerifyModal(false);
    setUtrId("");
    submissionDataRef.current = null;
  };

  const confirmAndSubmit = async () => {
    if (!utrId || utrId.trim() === "") {
      setError("Please enter the UTR/transaction id to continue.");
      return;
    }

    if (!submissionDataRef.current) {
      setError("Submission data unavailable. Please try again.");
      setShowVerifyModal(false);
      return;
    }

    setError("");
    setMessage("");
    setLoading(true);

    try {
      const token = getAccessToken();
      if (!token) {
        setError("Authentication token missing. Please log in again.");
        setLoading(false);
        return;
      }

      // append UTR and amount — use dot notation so backend frameworks map to nested object
      submissionDataRef.current.append("paymentDetails.utrId", utrId.trim());
      submissionDataRef.current.append("paymentDetails.amount", "299");

      const response = await promoterApi.register(submissionDataRef.current);

      if (response) {
        setMessage(
          "✅ Promoter created successfully! They can now login with their email and password."
        );
        form.current.reset();
        setPassword("");
        setConfirmPassword("");
        setUtrId("");
        submissionDataRef.current = null;
        setShowVerifyModal(false);
      }
    } catch (error) {
      console.error("Create promoter error:", error);
      let errorMessage = "Failed to create promoter. Please try again.";
      if (error?.status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.code === "UNAUTHORIZED") {
        errorMessage =
          "Unauthorized: Please ensure you are logged in as an approved Channel Partner.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form
        ref={form}
        onSubmit={handleSubmit}
        className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 py-10 px-4"
      >
        <div className="w-full max-w-3xl bg-white text-gray-900 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-200 to-gray-200 p-6 text-center text-gray-900">
            <h1 className="text-3xl font-bold uppercase tracking-wide">
              Create Promoter
            </h1>
            <p className="text-gray-900 text-sm mt-1">
              Fill in the details to register as a promoter
            </p>
          </div>

          {/* Messages */}
          {error && (
            <div className="mx-8 mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
              {(error.includes("network") ||
                error.includes("UNAUTHORIZED") ||
                error.includes("NOT_APPROVED")) && (
                <div className="mt-2 text-sm">
                  <p>
                    <strong>Note:</strong> Only approved Channel Partners can
                    create promoters.
                  </p>
                  <p>
                    Please complete your Channel Partner profile and get it
                    approved by admin before creating promoters.
                  </p>
                  <p className="mt-2">
                    <strong>Steps:</strong>
                  </p>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>Complete your profile in the Profile section</li>
                    <li>Submit your profile for approval</li>
                    <li>Wait for admin approval</li>
                    <li>Then you can create promoters</li>
                  </ol>
                </div>
              )}
            </div>
          )}
          {message && (
            <div className="mx-8 mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {message}
            </div>
          )}

          {/* Form Fields */}
          <div className="flex flex-col w-full px-8 pb-2 pt-4 gap-6">
            {/* Name & Father Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                name="name"
                type="text"
                placeholder="Full Name"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
              <input
                name="father_name"
                type="text"
                placeholder="Father Name"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
            </div>

            {/* Address Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                name="street"
                type="text"
                placeholder="Street Address"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
              <input
                name="city"
                type="text"
                placeholder="City"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <input
                name="state"
                type="text"
                placeholder="State"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
              <input
                name="pincode"
                type="text"
                placeholder="Pincode"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
              <select
                name="country"
                required
                className="border border-gray-400 p-2 rounded-md"
              >
                <option value="India">India</option>
              </select>
            </div>

            {/* Blood Group + Email + Phone */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <select
                name="blood_group"
                className="border border-gray-400 p-2 rounded-md w-full"
                required
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </select>
              <input
                name="email"
                type="email"
                placeholder="Email Address"
                required
                className="border border-gray-400 p-2 rounded-md"
              />
              <input
                name="phone_number"
                type="tel"
                placeholder="Phone Number (10 digits)"
                maxLength={10}
                required
                className="border border-gray-400 p-2 rounded-md"
              />
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              10-digit Indian mobile number (will be automatically formatted as
              +91xxxxxxxxxx)
            </p>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full border border-gray-400 p-2 pr-10 rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full border border-gray-400 p-2 pr-10 rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Qualification + Upload */}
            <div>
              <select
                name="qualification"
                required
                className="border border-gray-400 p-2 rounded-md w-full"
              >
                <option value="">Select Qualification</option>
                <option value="highschool">High School (10th)</option>
                <option value="intermediate">Intermediate (12th)</option>
                <option value="graduate">Graduate</option>
                <option value="postgraduate">Post Graduate</option>
              </select>
              <div className="mt-2">
                <label className="text-sm text-gray-700 font-semibold">
                  Upload Qualification Document
                </label>
                <input
                  name="qualification_doc"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  className="border border-gray-300 p-2 rounded-md w-full text-sm mt-1"
                />
              </div>
            </div>

            {/* Experience */}
            <input
              name="experience"
              type="text"
              placeholder="Experience"
              required
              className="border border-gray-400 p-2 rounded-md w-full"
            />

            {/* PAN Card + ID Type + Upload */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <input
                  name="pancard_number"
                  type="text"
                  placeholder="PAN Number"
                  required
                  className="border border-gray-400 p-2 rounded-md w-full"
                />
                {/* PAN Upload */}
                <div>
                  <label className="text-sm text-gray-700 font-semibold">
                    Upload PAN Card
                  </label>
                  <input
                    name="pancard_file"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    required
                    className="border border-gray-300 p-2 rounded-md w-full text-sm mt-1"
                  />
                </div>
              </div>
              <div>
                <select
                  name="id_type"
                  required
                  className="border border-gray-400 p-2 rounded-md w-full"
                >
                  <option value="">Select ID Type</option>
                  <option value="Aadhar Card">Aadhar Card</option>
                  <option value="Passport">Passport</option>
                  <option value="Voter ID">Voter ID</option>
                </select>
                <div>
                  <label className="text-sm text-gray-700 font-semibold">
                    Upload YOUR DOC.
                  </label>
                  <input
                    name="id_upload"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    required
                    className="border border-gray-300 p-2 rounded-md w-full text-sm"
                  />
                </div>
              </div>
            </div>

            {/* CV Upload */}
            <div>
              <label className="text-sm text-gray-700 font-semibold">
                Upload CV
              </label>
              <input
                name="cv"
                type="file"
                accept=".pdf,.doc,.docx"
                required
                className="border border-gray-300 p-2 rounded-md w-full text-sm mt-1"
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label className="text-sm text-gray-700 font-semibold">
                Upload Your Photo
              </label>
              <input
                name="photo_upload"
                type="file"
                accept=".jpg,.jpeg,.png"
                required
                className="border border-gray-300 p-2 rounded-md w-full text-sm mt-1"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || showVerifyModal}
              className={`mt-6 w-full font-semibold p-4 rounded-lg text-xl uppercase tracking-wider transition-all duration-300 ${
                loading
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-gray-300 hover:bg-gray-800 text-gray-800 hover:text-white"
              }`}
            >
              {loading ? "Processing..." : "Create Promoter"}
            </button>
          </div>
        </div>
      </form>

      {/* Verification Modal */}
      {showVerifyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={cancelVerification} // click outside cancels & cancels creation
        >
          <div
            className="w-full max-w-md bg-white rounded-lg shadow-lg p-6"
            onClick={(e) => e.stopPropagation()} // prevent overlay click when clicking modal
          >
            <h2 className="text-lg font-bold mb-3">
              Verify to Create Promoter
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Scan the QR or enter the verification code below to continue.
            </p>

            <div className="flex flex-col items-center gap-3 mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                  qrData
                )}`}
                alt="Payment QR code for ₹299"
                className="border p-2 bg-white"
              />
              <p className="text-sm font-medium text-gray-700">
                Pay ₹299 to continue
              </p>
              <p className="text-xs text-gray-500">
                Scan the QR with a UPI app to pay. After payment, enter the
                verification code you receive.
              </p>
            </div>

            <input
              value={utrId}
              onChange={(e) => setUtrId(e.target.value)}
              placeholder="Enter verification code"
              className="w-full border p-2 rounded-md mb-4"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (utrId.trim()) confirmAndSubmit();
                }
              }}
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelVerification}
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndSubmit}
                className={`px-4 py-2 rounded ${
                  utrId.trim()
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-700 cursor-not-allowed"
                }`}
                type="button"
                disabled={!utrId.trim() || loading}
              >
                {loading ? "Creating..." : "Confirm & Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatePromoter;
